import type { components } from '@/shared/api/schema'

export type Instruction = components['schemas']['PaymentInstructionOutput']
export type PaymentSource = (components['schemas']['PaymentSourceOutput'] & { source_kind?: 'invoice' })
  | (components['schemas']['ServicePaymentSourceOutput'] & { source_kind: 'service' })
  | (components['schemas']['FreightPaymentSourceOutput'] & { source_kind:'freight' })
export const sourceKey = (source: PaymentSource) => `${source.source_kind ?? 'invoice'}:${source.id}`
export type PaymentProof = components['schemas']['PaymentProofOutput']
export type Portion = components['schemas']['PaymentPortionInput']
export type PaymentAction = 'submit' | 'cancel' | 'approve' | 'reject' | 'withdraw' | 'begin' | 'result' | 'allocate'
export const paymentBase = '/api/v1/finance/payment-instructions'
export const paymentReadPermissions = ['finance.view_paymentinstruction', 'finance.view_payment',
  'finance.view_payable', 'procurement.view_purchase_amount', 'masterdata.view_supplier_finance']
const actions: Record<PaymentAction, string> = {
  submit: 'submit_paymentinstruction', cancel: 'submit_paymentinstruction', withdraw: 'submit_paymentinstruction',
  approve: 'review_paymentinstruction', reject: 'review_paymentinstruction', begin: 'execute_paymentinstruction',
  result: 'execute_paymentinstruction', allocate: 'allocate_payment',
}
export function paymentPermission(path: string) {
  if (path === paymentBase) return 'finance.add_paymentinstruction'
  const action = /^\/api\/v1\/finance\/payment-instructions\/[1-9]\d*\/([a-z]+)$/.exec(path)?.[1]
  return action && Object.hasOwn(actions, action) ? 'finance.' + actions[action as PaymentAction] : ''
}
export type PendingPayment = { path: string; body: string; key: string }
export function parsePaymentPending(raw: string): PendingPayment {
  const value = JSON.parse(raw) as PendingPayment
  if (!value || typeof value.path !== 'string' || !paymentPermission(value.path)
    || typeof value.body !== 'string' || value.body.length > 100000
    || typeof value.key !== 'string' || !/^[0-9a-f-]{36}$/i.test(value.key)) throw new Error('pending')
  const body: unknown = JSON.parse(value.body)
  if (!body || typeof body !== 'object' || Array.isArray(body)) throw new Error('pending')
  return { path: value.path, body: value.body, key: value.key }
}
/** Scaled integer for UI validation only. The server remains the financial truth. */
export function paymentAmount(value: string): bigint {
  if (!/^\d{1,14}(?:\.\d{1,4})?$/.test(value)) throw new Error('amount')
  const [whole = '0', fraction = ''] = value.split('.')
  return BigInt(whole + fraction.padEnd(4, '0'))
}
export function portionPayload(rows: { source: PaymentSource; amount: string }[], limit: string): Portion[] {
  if (rows.length > 200 || new Set(rows.map(row => sourceKey(row.source))).size !== rows.length) throw new Error('source')
  let total = 0n
  const result = rows.map(row => {
    const amount = paymentAmount(row.amount)
    if (amount <= 0n || amount > paymentAmount(row.source.remaining_amount)) throw new Error('amount')
    total += amount
    return row.source.source_kind === 'service'
      ? { fee_line_id: row.source.id, amount: row.amount }
      : row.source.source_kind==='freight' ? {freight_id:row.source.id,amount:row.amount}
        : { match_id: row.source.id, amount: row.amount }
  })
  if (total > paymentAmount(limit)) throw new Error('balance')
  return result.sort((a, b) => (a.match_id ?? 0) - (b.match_id ?? 0)
    || (a.fee_line_id ?? 0) - (b.fee_line_id ?? 0) || (a.freight_id ?? 0) - (b.freight_id ?? 0))
}
